Retorna o detalhe público de uma chamada específica da organização autenticada.

Use este endpoint para drill-down: diagnóstico técnico, contexto da campanha, metadados do destino, gravação, variáveis e dados coletados.

<ApiCodeExamples exampleId="callDetail" />

<ApiResponseExamples exampleId="callDetail" />

## Path parameter

- `id` (`uuid`, obrigatório): identificador da chamada.

## Resposta `200`

### Identificação e classificação

- `id` (`uuid`): identificador da chamada.
- `canonical_url` (`string`): link canônico da chamada no app Contact-Tel.
- `direction` (`string`): sentido da chamada.
  Valores possíveis:
  `outbound`, `inbound`
- `origin` (`string`): origem operacional.
  Valores possíveis:
  `campaign`, `manual`, `public_api`
- `call_type` (`string`): modalidade da chamada.
  Valores possíveis:
  `ai_agent`, `reverse_ivr`, `sip`
- `status` (`string`): status operacional.
  Valores possíveis:
  `pending`, `queued`, `dialing`, `analyzing`, `ringing`, `in_progress`, `finalizing`, `waiting_capacity`, `waiting_operating_hours`, `completed`, `no_interaction`, `failed`, `no_answer`, `no_answer_timeout`, `busy`, `cancelled`, `transfer_unanswered`, `congestion`, `voicemail`, `invalid_number`, `config_error`, `declined`, `unreachable`, `timeout`, `mismatch`, `api_timeout`, `terminated_manual`, `capacity_timeout`, `operating_window_timeout`

### Números e metadados do destino

- `to_number` (`string`): destino principal da chamada.
- `from_number` (`string`): origem da chamada.
- `from_number_real` (`string|null`): origem real observada no provider, quando disponível.
- `to_number_country_code` (`string`): código de país resolvido para o destino.
- `to_number_area_code` (`string`): DDD/área persistida do destino, quando aplicável.
- `to_number_phone_type` (`string`): tipo canônico do destino.
  Valores possíveis:
  `unknown`, `mobile`, `landline`, `sip`
- `to_number_state_code` (`string`): UF/estado do destino, quando resolvido.
- `to_number_state_name` (`string`): nome do estado do destino, quando resolvido.
- `to_number_region_code` (`string`): código de região do destino, quando resolvido.
- `to_number_region_name` (`string`): nome da região do destino, quando resolvido.
- `to_number_timezone` (`string`): timezone persistido do destino, quando resolvido.
- `sip_trunk_id` (`uuid|null`): identificador do SIP trunk associado.
- `sip_trunk_name` (`string|null`): nome do SIP trunk associado.
- `sip_trunk_pool_id` (`uuid|null`): identificador do pool de saída usado.
- `sip_trunk_pool_name` (`string|null`): nome do pool de saída usado.
- `sip_trunk_pool_member_id` (`uuid|null`): identificador do membro do pool selecionado.
- `sip_trunk_outbound_route_id` (`uuid|null`): identificador da rota de saída usada.
- `sip_trunk_outbound_route_name` (`string|null`): nome da rota de saída usada.
- `routing_decision` (`object`): snapshot público da decisão de roteamento de saída, quando aplicável.
- `phone_label` (`string|null`): rótulo público do número, quando existir.

### Consumo e outcome

- `duration` (`integer`): duração líquida em segundos.
- `ai_billed_seconds` (`integer`): segundos faturáveis líquidos de IA.
- `ai_gross_billed_seconds` (`integer`): segundos faturáveis brutos de IA.
- `ai_refunded_seconds` (`integer`): segundos de IA estornados/refundados.
- `telephony_billed_seconds` (`integer`): segundos faturáveis líquidos de telefonia.
- `telephony_gross_billed_seconds` (`integer`): segundos faturáveis brutos de telefonia.
- `telephony_refunded_seconds` (`integer`): segundos de telefonia estornados/refundados.
- `ring_duration` (`integer|null`): duração do ringing em segundos.
- `recording_download_url` (`string|null`): URL temporária para baixar a gravação armazenada, quando houver arquivo concluído.
- `is_converted` (`boolean`): indica se a chamada foi convertida.
- `is_optout` (`boolean`): indica se terminou com opt-out.
- `is_mismatch` (`boolean`): indica se terminou com mismatch.
- `termination_origin` (`string|null`): origem canônica do término.
  Valores possíveis:
  `unknown`, `pstn`, `agent`, `manual`, `system`, `internal`

### Linha do tempo

- `scheduled_at` (`datetime|null`): instante em que a chamada foi agendada.
- `queued_at` (`datetime|null`): instante de entrada na fila.
- `ringing_at` (`datetime|null`): instante de início do ringing.
- `initiated_at` (`datetime|null`): instante de iniciação.
- `answered_at` (`datetime|null`): instante de atendimento/conexão.
- `ended_at` (`datetime|null`): instante de término.
- `created_at` (`datetime`): instante de criação.
- `updated_at` (`datetime`): instante da última atualização.

### Contexto da campanha e do agente

- `campaign_id` (`uuid|null`): identificador da campanha associada.
- `campaign_name` (`string|null`): nome da campanha associada.
- `campaign_contact_id` (`uuid|null`): identificador do contato associado.
- `campaign_contact_display_name` (`string|null`): nome exibido do contato associado.
- `public_api_call_contact_id` (`uuid|null`): job pai público associado, quando a chamada veio da API pública.
- `client_reference` (`string|null`): referência opaca definida pelo cliente no job pai, ou `null` quando não aplicável.
- `public_api_call_contact_status` (`string|null`): status atual do job pai público.
- `retry_policy` (`object|null`): snapshot da política durável do job pai, ou `null` no fluxo legado/não público.
- `attempts` (`object|null`): progresso agregado do job pai, com `count`, `remaining` e `next_attempt_at`.
- `public_api_call_contact_phone_id` (`uuid|null`): telefone/tentativa do job público associado.
- `public_api_call_contact_phone_status` (`string|null`): status atual do telefone dentro do job público.
- `public_api_call_contact_phone_position` (`integer|null`): posição do telefone no job público.
- `agent_id` (`uuid|null`): identificador do agente vinculado.
- `agent_name` (`string|null`): nome do agente vinculado.
- `variables` (`object`): variáveis resolvidas da chamada/contato usadas no contexto de execução, sem variáveis internas `sys__*`.
- `created_by_id` (`uuid|null`): identificador do usuário que criou/originou a chamada, quando existir.
- `created_by_name` (`string|null`): nome exibível do usuário que criou/originou a chamada.
- `created_by_extension` (`string|null`): ramal do usuário criador/originador, quando existir.

### Transferência e participantes humanos

- `telephony_transfer_started_at` (`datetime|null`): instante em que a transferência humana foi iniciada.
- `telephony_transfer_wait_started_at` (`datetime|null`): instante em que a chamada entrou em espera por humano.
- `telephony_transfer_connected_at` (`datetime|null`): instante em que a perna humana foi conectada.
- `telephony_transfer_wait_ms` (`integer|null`): tempo de espera por humano em milissegundos.
- `transfer_destination_type` (`string|null`): tipo do destino usado na transferência.
- `transfer_destination_value` (`string|null`): ramal ou destino usado na transferência.
- `transfer_target_user_id` (`uuid|null`): identificador do usuário alvo da transferência.
- `transfer_target_user_name` (`string|null`): nome exibível do usuário alvo.
- `transfer_target_user_extension` (`string|null`): ramal do usuário alvo.
- `transfer_target_group_id` (`uuid|null`): identificador do grupo alvo da transferência.
- `transfer_target_group_name` (`string|null`): nome exibível do grupo alvo.
- `interaction_claimed_by_id` (`uuid|null`): usuário que assumiu a interação humana.
- `interaction_claimed_by_name` (`string|null`): nome exibível de quem assumiu a interação.
- `interaction_claimed_at` (`datetime|null`): instante do claim.
- `interaction_answered_by_id` (`uuid|null`): usuário que atendeu a interação humana.
- `interaction_answered_by_name` (`string|null`): nome exibível de quem atendeu.
- `interaction_answered_at` (`datetime|null`): instante do atendimento humano.
- `interaction_answered_extension` (`string|null`): ramal que atendeu a interação humana.

### Origem operacional e IVR

- `origin_line_name` (`string|null`): nome da linha/origem operacional persistida.
- `ivr_selected_digit` (`string|null`): dígito DTMF selecionado no fluxo IVR, quando houver.
- `ivr_selected_status` (`string|null`): status resolvido a partir do mapa DTMF da campanha. Não existe enum global fixa.
- `ivr_resolution` (`string|null`): resumo textual da resolução IVR persistida.
- `ivr_audio_heard_seconds` (`integer|null`): segundos do áudio IVR efetivamente ouvidos.
- `ivr_audio_heard_to_end` (`boolean|null`): indica se o áudio IVR chegou ao fim.

### Dados de conversa e segmentos

- `data_collected` (`object|null`): dados conversacionais consolidados dos segmentos públicos da chamada. Cada item retorna apenas `value`, `rationale` e `__source` sanitizado.
- `segments` (`array<object>`): segmentos públicos da chamada, em ordem de execução.
- `segments[].id` (`uuid`): identificador público do segmento.
- `segments[].segment_type` (`string`): tipo do segmento. Consulte a referência de opções abaixo.
- `segments[].sequence` (`integer`): ordem do segmento dentro da chamada.
- `segments[].started_at`, `segments[].connected_at`, `segments[].ended_at` (`datetime|null`): linha do tempo do segmento.
- `segments[].duration_seconds` (`integer|null`): duração do segmento.
- `segments[].billed_seconds`, `segments[].gross_billed_seconds`, `segments[].refunded_seconds` (`integer`): visão de consumo do segmento.
- `segments[].recording_download_url` (`string|null`): URL temporária para baixar a gravação do segmento, quando houver arquivo concluído.
- `segments[].transcript` (`string`): transcrição simples.
- `segments[].transcript_full` (`array|object|null`): transcrição estruturada sanitizada. Cada item retorna apenas `role`, `message`, `original_message` e `time_in_call_secs`.
- `segments[].conversation_summary` (`string|null`): resumo conversacional do segmento.
- `segments[].ivr_dtmf_digit`, `segments[].ivr_dtmf_received_at`, `segments[].ivr_dtmf_mapped_status`, `segments[].ivr_end_reason`: evidências IVR do segmento, quando houver.
- `segments[].voicemail_detection` (`object|null`): resultado público da detecção de caixa postal, quando houver.

### Referência de opções

#### `direction`

- `outbound`: chamada originada pela plataforma para um destino externo.
- `inbound`: chamada recebida pela plataforma.

#### `origin`

- `campaign`: chamada criada por campanha.
- `manual`: chamada iniciada manualmente por usuário/operador.
- `public_api`: chamada criada por integração via API pública.

#### `call_type`

- `ai_agent`: chamada conduzida por agente de IA.
- `reverse_ivr`: chamada de URA reversa com áudio e captura DTMF.
- `sip`: chamada SIP convencional sem agente de IA como condutor principal.

#### `status`

- `pending`: chamada criada e aguardando enfileiramento.
- `queued`: chamada na fila de execução.
- `dialing`: discagem em andamento.
- `analyzing`: etapa pré-atendimento/análise em andamento.
- `ringing`: destino tocando.
- `in_progress`: chamada atendida e em andamento.
- `finalizing`: chamada encerrando e consolidando dados.
- `completed`: chamada concluída com sucesso operacional.
- `no_interaction`: houve atendimento, mas sem interação útil do usuário.
- `failed`: falha operacional genérica.
- `no_answer`: destino não atendeu.
- `no_answer_timeout`: timeout aguardando atendimento.
- `busy`: destino ocupado.
- `cancelled`: chamada cancelada antes de concluir.
- `transfer_unanswered`: transferência não atendida.
- `congestion`: rede/rota congestionada.
- `voicemail`: chamada caiu em caixa postal.
- `invalid_number`: número inválido ou inexistente.
- `config_error`: erro de configuração da campanha, agente ou telefonia.
- `declined`: destino recusou a chamada.
- `unreachable`: destino temporariamente indisponível.
- `timeout`: timeout técnico da requisição/chamada.
- `mismatch`: pessoa errada atendeu.
- `api_timeout`: timeout de API/provider durante a execução.
- `terminated_manual`: encerramento manual por operador/admin.
- `waiting_capacity`: chamada aceita pela API pública e aguardando slot de concorrência.
- `waiting_operating_hours`: chamada aceita pela API pública e aguardando horário operacional.
- `capacity_timeout`: chamada pública expirou aguardando capacidade.
- `operating_window_timeout`: chamada pública expirou aguardando janela operacional.

#### `to_number_phone_type`

- `unknown`: tipo do destino não resolvido.
- `mobile`: número móvel.
- `landline`: telefone fixo.
- `sip`: ramal/endereço SIP.

#### `termination_origin`

- `unknown`: origem do término não identificada.
- `pstn`: término causado pelo lado de telefonia/PSTN.
- `agent`: término solicitado pelo agente de IA.
- `manual`: término manual por usuário/operador.
- `system`: término automático pelo sistema.
- `internal`: término causado por endpoint interno.

#### `segments[].segment_type`

- `ai`: conversa com agente de IA.
- `telephony_internal_extension`: transferência para ramal interno.
- `telephony_outbound_internal_extension`: saída para ramal interno em fluxo outbound.
- `telephony_external_extension`: transferência para ramal externo.
- `telephony_external_phone`: transferência para telefone externo.
- `ivr`: segmento de URA reversa.

## Erros esperados

- `401 Unauthorized`: token inválido, expirado, revogado, ausente ou bloqueado por IP.
- `403 Forbidden`: a credencial autenticou, mas não possui `calls.read`.
- `404 Not Found`: a chamada não existe ou não pertence à organização autenticada.

## Quando usar este endpoint

- consultar uma chamada específica a partir do `id` vindo da listagem;
- abrir drill-down técnico/operacional em sistemas externos;
- depurar status e desfecho de uma chamada específica sem usar a tela administrativa.
